/**
 * Rate limit configuration for all endpoints
 *
 * Multi-tier rate limiting:
 * - Layer 1: Cloudflare native (IP-based, fast, at edge)
 * - Layer 2: Redis (user-based, granular, for authenticated endpoints)
 */

// From `route-paths`, a contract that imports nothing: this module is on the root barrel, which
// every Worker isolate loads (`route-paths` says why the paths live apart from their contracts).
import {
  AUTOMATION_WEBHOOK_MOUNT,
  MANAGED_AGENTS_WEBHOOK_MOUNT,
  BASE_DESTRUCTION_CONFIRM_PATH,
  BASE_DESTRUCTION_PREFIX,
  COMMUNITY_EMAIL_UNSUBSCRIBE_PATH,
  CONTACT_BOOK_EMAIL_UNSUBSCRIBE_PATH,
  DATA_PROXY_PREFIX,
} from './contracts/route-paths';

/**
 * Rate limit tiers using Cloudflare bindings
 */
export const RATE_LIMIT_TIERS = {
  /** Very restrictive - OAuth, auth endpoints (5/min) */
  STRICT: { limit: 5, period: 60, bindingName: 'RATE_LIMITER_STRICT' },
  /** Moderate - billing, token exchange (30/min) */
  MODERATE: { limit: 30, period: 60, bindingName: 'RATE_LIMITER_MODERATE' },
  /** Standard - general API endpoints (100/min) */
  STANDARD: { limit: 100, period: 60, bindingName: 'RATE_LIMITER_STANDARD' },
  /** High throughput - message webhooks (300/min) */
  HIGH: { limit: 300, period: 60, bindingName: 'RATE_LIMITER_HIGH' },
  /**
   * A vendor's signed deliveries, whose few addresses carry every tenant's: one per hosted tool
   * round (#5083 decision 166), so well above what HIGH allows one address (3,000/min).
   */
  BULK: { limit: 3000, period: 60, bindingName: 'RATE_LIMITER_BULK' },
} as const;

export type RateLimitTier = keyof typeof RATE_LIMIT_TIERS;

/**
 * Configuration for a specific endpoint's rate limiting
 */
export interface EndpointRateLimitConfig {
  /** IP-based tier using Cloudflare binding */
  tier: RateLimitTier;
  /** User-based limit (requests per minute) - only for authenticated requests */
  userLimit?: number;
  /** Window in seconds for user limit (default: 60) */
  userWindow?: number;
  /** If true, return 503 when Redis is unavailable (default: false = fail-open) */
  failClosed?: boolean;
  /** If true, skip IP rate limit for authenticated users (default: false) */
  skipAuthenticatedUsers?: boolean;
  /**
   * Canonical identity for the per-user budget. Without it the budget is counted per request
   * path, so set it whenever the matched paths carry ids or other caller-chosen segments.
   */
  userLimitKey?: string;
}

/**
 * Endpoint configurations
 * Keys are path patterns that support wildcards (*)
 */
const ENDPOINT_CONFIGS: Record<string, EndpointRateLimitConfig> = {
  // OAuth callbacks - strict, fail-closed (prevent brute force)
  '/oauth/callback/*': { tier: 'STRICT', failClosed: true },

  // Auth endpoints - strict, fail-closed
  '/auth/phone': { tier: 'STRICT', failClosed: true },
  '/auth/mcp/exchange': { tier: 'MODERATE', failClosed: true },
  // Native magic-code login (pre-auth) — STRICT per-IP bounds a single host; WorkOS owns
  // the per-email lockout / TTL / single-use guarantees.
  '/api/auth/magic-code/*': { tier: 'STRICT', failClosed: true },
  // Native password login (pre-auth) — same posture as magic-code above. STRICT per-IP bounds a
  // single host but FAILS OPEN on a binding error (like every IP limit); the `failClosed` flag
  // here is inert on a pre-auth route (it's only enforced on the per-user branch, which needs an
  // authenticated caller). The fail-CLOSED brute-force guarantee for the static credential comes
  // from the route's per-email caps (burst + cumulative hourly), not this flag.
  '/api/auth/password/*': { tier: 'STRICT', failClosed: true },

  // Every `/api/*` group whose paths carry ids pins `userLimitKey` (gh #4756), held there by
  // `security/endpoint-invariants.test.ts`.

  // Billing - moderate, fail-closed with user limits
  '/api/billing/*': {
    tier: 'MODERATE',
    userLimit: 100,
    failClosed: true,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/billing/*',
  },
  '/webhooks/stripe': { tier: 'MODERATE', failClosed: true },
  '/webhooks/workos': { tier: 'MODERATE', failClosed: true },

  // API endpoints - standard with user limits
  // 500 because the web's settings live updates refetch per hub in every open tab, whatever page
  // it shows (`settingsLiveUpdateStore.ts`): one batch of changes costs about 10 + 4 per agent
  // whose tools were opened + 2 per loaded resource, so a CLI push and publish (both hubs) with
  // two tabs open passes 150 in a minute. A multi-file resource upload adds 2–5 per file.
  '/api/setup/*': {
    tier: 'STANDARD',
    userLimit: 500,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/setup/*',
  },
  // 500 because an eval run's drill-down (`SessionResults.tsx`) fetches its list and then every
  // LLM turn's record at once: up to 201 requests (`OBSERVABILITY_LIST_CAP`, 200). Sending keeps
  // the 150 it had when each path held its own budget, on the exact key below.
  '/api/conversations/*': {
    tier: 'STANDARD',
    userLimit: 500,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/conversations/*',
  },
  '/api/conversations/send-message': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/users/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/analytics/*': {
    tier: 'STANDARD',
    userLimit: 150,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/analytics/*',
  },
  // 500 because `wayai eval-results --eval` reads completed sessions one by one, newest first,
  // until one holds the eval (up to the 1000 the list returns). Tenant SQL keeps the 150 it had
  // when each path held its own budget: each query may run 30 s on shared-tenant ClickHouse
  // after its caller has gone (`sql-validator.ts`, stage 10b).
  '/api/evals/*': {
    tier: 'STANDARD',
    userLimit: 500,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/evals/*',
  },
  '/api/evals/sql': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  // An eval run's calls (gh #4418), kept off the end user's call budgets below. Create keeps the
  // 150 it had: it opens a provider session on the org's own key, billed per second. The routes
  // on one call take a budget of their own so no eval read can spend it: a refused hang-up
  // leaves the call running, and billing. 500 because a call suite polls each run's record
  // every second, about 64 requests a minute per suite.
  '/api/evals/calls': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/evals/calls/*': {
    tier: 'STANDARD',
    userLimit: 500,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/evals/calls/*',
  },
  '/api/files/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  // The org contact book (wayai#4931 PR 2b). A CSV import sends one request per
  // `CONTACT_IMPORT_BATCH_MAX` rows, so 150 a minute imports 75,000 rows a minute.
  '/api/contact-book/*': {
    tier: 'STANDARD',
    userLimit: 150,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/contact-book/*',
  },
  // An automation email's one-click unsubscribe (wayai#4931 PR 2d) — public: its token is its one
  // credential, so this per-IP limit is its one bound, and runs before the route reads anything.
  // MODERATE (30/min per IP), as the community email's unsubscribe, for the same reason: mail
  // clients' one-click unsubscribe POSTs it from their own servers, which many recipients' clicks
  // share. An exact key, so it wins over the wildcard above.
  [CONTACT_BOOK_EMAIL_UNSUBSCRIBE_PATH]: { tier: 'MODERATE' },
  '/api/automations/*': {
    tier: 'STANDARD',
    userLimit: 150,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/automations/*',
  },
  // A hub's own contact lists (wayai#4931 PR 2c): config edits, at the automations' rate.
  '/api/hub-contact-lists/*': {
    tier: 'STANDARD',
    userLimit: 150,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/hub-contact-lists/*',
  },
  '/api/message-templates/*': {
    tier: 'STANDARD',
    userLimit: 150,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/message-templates/*',
  },
  '/api/live-updates/*': { tier: 'STANDARD', userLimit: 150, skipAuthenticatedUsers: true },
  '/api/hub-users/*': {
    tier: 'STANDARD',
    userLimit: 150,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/hub-users/*',
  },
  '/api/conversation-state/*': {
    tier: 'STANDARD',
    userLimit: 150,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/conversation-state/*',
  },

  // The authenticated byte route (`routes/files/r2.ts`), mounted after its `authMiddleware` so
  // the caller is known: each request reads the HubDO its key names, and a hub id never created
  // still wakes a Durable Object (gh #4747; since gh #4775 T5 one that writes nothing). The key is pinned
  // because the paths are file keys, which would otherwise each get a fresh budget (and one
  // over 256 characters would be refused outright, `checkUserLimitNative`).
  //
  // 500 a minute because the web chat fetches every attachment of every loaded message at
  // once, with no list virtualization: up to 50 per 50-message page, 200 per ended
  // conversation or history entry at one file per message, and 4 per message at most in its
  // image grid. Repeats come from the browser's HTTP cache (`private, max-age=3600`); mobile
  // renders through a disk cache and plays audio from `/files/signed`. The budget is shared by
  // a caller's tabs and devices. The other two byte routes, `/files/signed/*` and
  // `/files/public/*`, mount no limiter (the reason is at their entries in
  // `security/endpoint-invariants.test.ts`'s route registry).
  '/files/auth/*': {
    tier: 'STANDARD',
    userLimit: 500,
    skipAuthenticatedUsers: true,
    userLimitKey: '/files/auth/*',
  },

  // Channel webhooks - high throughput (external systems, never authenticated)
  '/webhooks/whatsapp/*': { tier: 'HIGH' },
  '/webhooks/instagram/*': { tier: 'HIGH' },
  // An automation's webhook deliveries (wayai#4931 D13) — no bearer token: the automation's
  // secret is the credential, checked against one HubDO read. This per-IP limit runs first, before
  // anything is read. HIGH, as the channel webhooks: senders are servers, and an integration
  // platform's egress addresses carry many tenants' deliveries. The per-automation budget is
  // `AUTOMATION_WEBHOOK_PER_AUTOMATION_LIMIT`, counted after the delivery is verified.
  [`${AUTOMATION_WEBHOOK_MOUNT}/*`]: { tier: 'HIGH' },
  // Managed Agents session events (#5083 Phase 4): one delivery per hosted tool round, and one
  // vendor address carries every tenant's, so BULK. The per-connection budget is
  // `MANAGED_AGENTS_WEBHOOK_PER_CONNECTION_LIMIT`, counted after the delivery is verified.
  [`${MANAGED_AGENTS_WEBHOOK_MOUNT}/*`]: { tier: 'BULK' },

  // Reports (CLI → platform_report triage queue). One per-user budget covers intake
  // (POST), the content edit (PATCH /:id), the reporter read-back (GET, GET /:id), and the
  // verification loop (POST /:id/accept, /:id/contest). The limiter is path-based, so a
  // method-level split (loose reads, strict writes) isn't expressible — GET /:id and PATCH
  // /:id share a path. So the budget is sized for the read-heavy poll loop (a reporter agent
  // polling `report get`/`list` while it waits for the fix), at 60/hour. Intake-flooding stays
  // bounded regardless: identical reports collapse on `fingerprint` (ON CONFLICT DO NOTHING),
  // so a runaway create loop merges rather than floods. Still STRICT tier + failClosed so a
  // genuinely runaway distinct-report loop surfaces as 429. The `/*` is load-bearing — an
  // exact `/api/reports` key would leave the `/:id` paths unmatched (→ unthrottled).
  // `userLimitKey` is equally load-bearing: without it, middleware falls back to each request's
  // path and report IDs/actions receive independent counters instead of this shared budget.
  '/api/reports/*': {
    tier: 'STRICT',
    userLimit: 60,
    userWindow: 3600,
    failClosed: true,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/reports/*',
  },

  // Admin report mutations (transition/group/ungroup/edit) — platform-admin
  // gated at the route, rate-limited here as defense-in-depth. Generous STANDARD
  // cap so legitimate triage bursts aren't throttled (grouping is one request
  // even for many members); deliberately NOT failClosed so a Redis blip never
  // blocks an admin's triage. The bare GET reads share this budget harmlessly.
  '/api/admin/reports/*': { tier: 'STANDARD', userLimit: 150, userLimitKey: '/api/admin/reports/*' },

  // GitHub webhook (issues.closed → platform_report addressed). MODERATE +
  // fail-closed; GitHub retries on transient failures so a brief Redis blip
  // doesn't lose state.
  '/webhooks/github': { tier: 'MODERATE', failClosed: true },

  // Account deletion — strict on the destructive POST + public undo. The
  // /api/users/* default tier above ALSO matches these, but `getEndpointConfig`
  // returns the longest-prefix match, so the more specific entries here win.
  // `skipAuthenticatedUsers` deliberately omitted: every caller is authenticated,
  // so including it would make `userLimit` a no-op — the per-user cap IS the gate.
  '/api/users/me/deletion': { tier: 'STRICT', userLimit: 3, userWindow: 3600, failClosed: true },
  '/api/users/me/deletion/preflight': { tier: 'STANDARD', userLimit: 30, failClosed: true },
  '/api/users/deletion/undo': { tier: 'STRICT', failClosed: true },

  // Production-base destruction.
  //
  // Keyed off the CONTRACT constants, not literals: `BASE_DESTRUCTION_CONFIRM_PATH`'s own
  // docstring promises that the mount, the `AUTH_BYPASS_PATHS` entry, the rate-limit key and
  // the URL the email embeds move together, and a literal here is exactly how that promise
  // goes quietly false — leaving an unauthenticated HMAC endpoint on the default tier.
  //
  // The emailed GET is PUBLIC, so it gets the IP-tier bound the pre-auth routes get.
  // `failClosed` is deliberately NOT set: it is only consulted on the per-USER branch, which
  // needs an authenticated caller, so on a pre-auth route it is inert — the same thing
  // `/api/auth/magic-code/send` above says about itself. The IP limiter fails OPEN on a
  // limiter error by design (`packages/runtime/src/rate-limiter.ts`), and claiming otherwise
  // would tell the next auditor a protection exists that does not.
  [BASE_DESTRUCTION_CONFIRM_PATH]: { tier: 'STRICT' },
  // The console's read, which every production base's settings pane issues on mount. Without
  // its own entry it inherits the mount-wide budget below, and an admin clicking through
  // thirty bases in an hour would be 429'd by a limit sized for irreversible writes.
  [`${BASE_DESTRUCTION_PREFIX}/destructions`]: { tier: 'STANDARD', userLimit: 150 },
  // The mutating routes: initiate, the authenticated confirm, and cancel. Per user rather than
  // per IP — all three are authenticated and org-admin gated — and hourly, since a single human
  // touching thirty production-deletion operations in an hour is already far past normal.
  // Longest-prefix wins, so the two entries above are not widened by this one. The key is
  // pinned so those thirty are counted across every base the caller touches (gh #4756).
  [`${BASE_DESTRUCTION_PREFIX}/*`]: {
    tier: 'STANDARD',
    userLimit: 30,
    userWindow: 3600,
    failClosed: true,
    userLimitKey: `${BASE_DESTRUCTION_PREFIX}/*`,
  },

  // Email-invite preview — public, fail-closed. MODERATE (30/min IP) leaves
  // headroom for the signup-page server-fetch + reloads + prefetches; STRICT
  // would throttle legitimate users opening the link in multiple tabs.
  '/api/invites/preview': { tier: 'MODERATE', failClosed: true },
  // "Don't email me again" (gh #4661) — public; one POST per click on its confirm page. STRICT
  // per IP. No `failClosed`: it is consulted only on the per-user branch, which a pre-auth route
  // never reaches (see `BASE_DESTRUCTION_CONFIRM_PATH` above).
  '/api/invites/unsubscribe': { tier: 'STRICT' },

  // Admin DO debug reads — the ONE deliberate deviation from the Data Explorer
  // catch-all below, and the only reason a per-route key still exists here.
  // Platform-admin is enforced at the route; this is the independent
  // defense-in-depth signal so a runaway investigation script surfaces as 429.
  //
  // STRICT is kept ON PURPOSE even though the catch-all moved off it: per IP, at 5/min vs
  // STANDARD's 100/min, walking 10k ids takes ~33h instead of ~100min — a detection window
  // worth keeping on the routes that return RAW DO ROWS.
  //
  // The cost, stated rather than elided: STRICT is one bucket shared with
  // `/oauth/callback/*` and `/api/auth/magic-code/*`, so a debug session from a
  // NAT'd office IP can 429 a colleague's login for up to a minute. That trade is
  // accepted for a rare, deliberate investigation surface — and is exactly why the
  // browse routes below are NOT here.
  //
  // The key is pinned, so a sweep of `/debug/do/hub/<id>/tables` across ids spends one
  // per-admin budget (gh #4756). 120 an hour: an agent's investigation of a dozen
  // conversations made 37 debug reads in its busiest hour.
  '/api/admin/data-explorer/debug/*': {
    tier: 'STRICT',
    userLimit: 120,
    userWindow: 3600,
    failClosed: true,
    userLimitKey: '/api/admin/data-explorer/debug/*',
  },
  // Catch-all for the rest of the Data Explorer (`/search`, `/entity/*`, `/do/*`,
  // `/kv/*`, the DELETE routes). Added as a catch-all rather than another per-route
  // key because this budget kept lagging behind routes joining the debug boundary:
  // with no match the path falls to the default, which sets no `userLimit`, so the
  // per-user layer never runs at all. `getEndpointConfig` takes the longest match,
  // so `debug/*` above still wins.
  //
  // `/entity/*` deliberately has NO key of its own: it needs precisely this config,
  // and a duplicate entry is how the two drift apart on the next edit.
  //
  // `tier` is deliberately STANDARD, not STRICT. `tier` is the per-IP knob and
  // STRICT is 5/min keyed on IP ALONE — one bucket shared across every STRICT
  // endpoint. On these browse routes that breaks the console (open Browse KV and
  // inspect four values = 6 requests; expand six org rows in the search results =
  // 6 `/entity/org/:id` calls) and would drain the same IP's budget for
  // `/oauth/callback/*` and `/api/auth/magic-code/*`, which on a NAT'd office IP
  // means all admins. The per-user hourly cap is the leg that matters here.
  //
  // The canonical key is deliberate: request paths contain DO ids and arbitrary
  // malformed requests can contain credential material. Keying on the request path
  // both stores that material in Redis/logs and gives every target a fresh counter.
  // One stable identity makes every browse request consume the same per-admin
  // budget without storing request targets.
  //
  // 600/hour preserves the original pagination requirement (about 30k rows at
  // 50/page) while now genuinely bounding id/key enumeration across the surface.
  '/api/admin/data-explorer/*': {
    tier: 'STANDARD',
    userLimit: 600,
    userWindow: 3600,
    failClosed: true,
    userLimitKey: '/api/admin/data-explorer/*',
  },

  // Admin harness incident-response (harness-agents PR 2.9b) — mass-destroy reaps
  // EVERY live harness session/token/slot in scope, so it gets a strict budget
  // (STRICT + a low per-user hourly cap + fail-closed). Platform-admin is enforced
  // at the route; this is the independent defense-in-depth signal so a runaway
  // reap loop surfaces as 429. The break-glass disable/enable rides through
  // `/api/admin/config` (its own STANDARD budget), not this key.
  '/api/admin/harness/*': {
    tier: 'STRICT',
    userLimit: 10,
    userWindow: 3600,
    failClosed: true,
    userLimitKey: '/api/admin/harness/*',
  },

  // Platform-wide team-orphan repair — a single call can WRITE across every hub of
  // every org (HubDO purge + UserDO mirror fan-out), so it gets the same strict,
  // fail-closed budget as the other cross-platform admin write. Exact path (not a
  // prefix): the sibling `/api/admin/hubs/:hubId/harness` flag flips stay on the
  // default admin budget. Platform-admin is enforced at the route; this is the
  // defense-in-depth signal that a repair loop surfaces as 429.
  '/api/admin/hubs/repair-team-orphans': { tier: 'STRICT', userLimit: 10, userWindow: 3600, failClosed: true },

  // Branching-orphan repair (#4577) — deletes named production rows, and its report mode
  // fans out over every production hub of every org, so it takes the same strict,
  // fail-closed budget as the team-orphan repair. Exact path, for the same reason.
  '/api/admin/hubs/repair-branching-orphans': { tier: 'STRICT', userLimit: 10, userWindow: 3600, failClosed: true },

  // CI/GitOps endpoints - standard with user limits. 500 because `wayai push` makes four requests
  // (pull, diff, push, pull) and a GitOps job may push every hub at once with one token; when
  // each path held its own 100, that ran to 100 hubs a minute.
  '/api/ci/*': { tier: 'STANDARD', userLimit: 500, skipAuthenticatedUsers: true, userLimitKey: '/api/ci/*' },

  // Attested `/v1` proxy onto the Rekor Worker. `userLimitKey` is load-bearing here: the
  // per-user budget defaults to the full request path, and this surface's paths carry base,
  // record, and file ids — so without a pinned key every distinct resource would get its own
  // fresh budget and the per-user limit would bound nothing. One shared budget across the
  // whole data surface is the intent.
  [`${DATA_PROXY_PREFIX}/*`]: {
    tier: 'STANDARD',
    userLimit: 150,
    skipAuthenticatedUsers: true,
    userLimitKey: `${DATA_PROXY_PREFIX}/*`,
  },

  // Voice calls — the end user's call routes (`routes/api/calls`). Every caller is signed
  // in, so the per-IP tier is skipped for them: Brazilian mobile carriers put many callers
  // behind one IP, and a shared bucket would refuse one caller for another's traffic.
  //
  // Create is the only route that spends: it opens a provider session on the org's own
  // key, billed per second. So it takes its own exact key and an hourly per-user cap, which
  // lives on Redis and is fail-closed like the other hourly caps. The exact key wins over
  // the wildcard below, which also matches `/api/calls` (see `getEndpointConfig`).
  '/api/calls': {
    tier: 'STANDARD',
    userLimit: 30,
    userWindow: 3600,
    failClosed: true,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/calls',
  },
  // The routes on one call: `ready`, hang-up, the delegation report and the status read; and
  // the opening read (`/api/calls/openings/:language`), one per call, before it exists.
  // NEVER fail-closed: a refused hang-up leaves the call running, and billing, until a
  // limit ends it. The 60 s budget runs on the native binding, which fails open. The key is
  // pinned because the paths carry the call id, which would otherwise give every call a
  // fresh budget.
  '/api/calls/*': {
    tier: 'STANDARD',
    userLimit: 150,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/calls/*',
  },

  // Contacts between accounts (gh #4661, `routes/api/contacts`). Every caller is signed in, so
  // the per-IP tier is skipped. The keys are pinned because the paths carry user and invite
  // ids, which would otherwise give each id a fresh budget. Blocks take their own, lower
  // budget (#4661 Limits: blocks are rate-limited). The invites-per-day cap and every list
  // cap are counted in the caller's UserDO, in the same write they bound, not here.
  '/api/contacts/*': {
    tier: 'STANDARD',
    userLimit: 60,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/contacts/*',
  },
  '/api/contacts/blocks/*': {
    tier: 'STANDARD',
    userLimit: 30,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/contacts/blocks/*',
  },
  // Person-to-person threads (gh #4661, `routes/api/threads`): the per-user send limit, 60 a
  // minute on the native binding. Every thread route shares the one budget, reads included:
  // the paths carry thread and message ids, so the key is pinned, and a pattern here cannot
  // tell a send from a read of the same path. Like every 60 s budget it fails open on a
  // binding error, so it bounds sends in the steady state only.
  '/api/threads/*': {
    tier: 'STANDARD',
    userLimit: 60,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/threads/*',
  },
  // An org admin's erasure of one person's thread messages (gh #4661 owner decision 7): any user
  // can own a free org, so each request arms that org's alarm, and this caps how fast one person
  // can ask. The org's own daily cap (`PEOPLE_ORG_ERASURES_PER_DAY`, counted in its OrgDO in the
  // write it bounds) is the fail-closed bound; this budget, on the native binding, fails open.
  '/api/threads/org-erasures': {
    tier: 'STANDARD',
    userLimit: 10,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/threads/org-erasures',
  },
  // A person's report of a message, thread or person (`people-reports.ts`): each one written
  // arms the PlatformDO's alarm and emails the platform admins, so it takes its own, lower
  // budget. The reporter's daily cap (`PEOPLE_REPORTS_PER_DAY`, counted in the report store in
  // the write it bounds) is the fail-closed bound; this budget, on the native binding, fails open.
  '/api/threads/reports': {
    tier: 'STANDARD',
    userLimit: 10,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/threads/reports',
  },

  // Communities (gh #4872, `routes/api/communities`): every signed-in route under the mount (the
  // preview below excepted) shares one per-user budget, which is also each member's send limit and
  // bounds the reads that scan a community's rows (a title search, the stats); a community's own
  // limits (its rate, the daily caps, slow mode) are counted in its CommunityDO, in the write they
  // bound. Every caller is signed in, so the per-IP tier is skipped; the key is pinned because the
  // paths carry community, room and message ids.
  '/api/communities/*': {
    tier: 'STANDARD',
    userLimit: 60,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/communities/*',
  },
  // The join page's preview of a link — public, fetched from the visitor's browser, so this
  // per-IP limit is its one bound and runs before the route reads anything (#4872
  // owner-delegated decision 9). MODERATE (30/min per IP) leaves room for reloads and several
  // tabs. An exact key, so it wins over the wildcard above.
  '/api/communities/join-preview': { tier: 'MODERATE' },
  // An announcement email's unsubscribe (#4872 PR 8a) — public: its token is its one credential, so
  // this per-IP limit is its one bound, and runs before the route reads anything. MODERATE (30/min
  // per IP), not STRICT: mail clients' one-click unsubscribe POSTs it from their own servers, which
  // many members' clicks share, and STRICT's 5/min bucket is shared with sign-in. An exact key, so
  // it wins over the wildcard above.
  [COMMUNITY_EMAIL_UNSUBSCRIBE_PATH]: { tier: 'MODERATE' },

  // The WayAI Guide's help entry (wayai#4948, `routes/api/guide.ts`): one POST per click by a viewer
  // the snapshot does not show enrolled, which reads the person's row on the Guide hub and writes only
  // what is missing (an enrolled viewer's click opens the hub without a request). Every caller is
  // signed in, so the per-IP tier is skipped; 10 a minute per person on the native binding (fails
  // open, like every 60 s budget) bounds what a looping client costs the one Guide HubDO.
  '/api/guide/access': {
    tier: 'STANDARD',
    userLimit: 10,
    skipAuthenticatedUsers: true,
    userLimitKey: '/api/guide/access',
  },

  // App channel - high throughput with user limits
  '/channels/app/*': { tier: 'HIGH', userLimit: 500, skipAuthenticatedUsers: true },

  // API channel (api-channel-connector) - machine-to-machine, high throughput
  '/channels/api/*': { tier: 'HIGH', userLimit: 500, skipAuthenticatedUsers: true },

  // System/scheduled - high throughput (internal)
  '/channels/system/*': { tier: 'HIGH', skipAuthenticatedUsers: true },
  '/scheduled/*': { tier: 'HIGH', skipAuthenticatedUsers: true },

  // WebSocket - standard with user limit to prevent connection spam
  '/live-updates/ws': { tier: 'STANDARD', userLimit: 100, failClosed: true },
  // A community's live socket (gh #4872 PR 5, `GET /communities/:communityId/live`): outside
  // `/api/*`, with no bearer token, so its per-IP tier is its one bound before the CommunityDO
  // checks the ticket. STANDARD, as `/live-updates/ws`: a client opens one per community it shows
  // and reconnects within the hour.
  '/communities/*': { tier: 'STANDARD' },

  // WS ticket mint - per-user 60/min accommodates aggressive reconnect patterns
  // without opening abuse vectors; fail-closed so Redis outages don't let
  // runaway clients mint unbounded tickets.
  '/api/auth/ws-ticket': { tier: 'MODERATE', userLimit: 60, failClosed: true, skipAuthenticatedUsers: true },
  '/api/auth/session-check': { tier: 'STANDARD', userLimit: 120, skipAuthenticatedUsers: true },
  '/api/auth/logout': { tier: 'MODERATE', userLimit: 10, failClosed: true, skipAuthenticatedUsers: true },
};

/**
 * Verified deliveries one webhook automation accepts per minute (wayai#4931 D13), counted on the
 * native per-user binding of this budget keyed by the automation's id, after the signature or
 * static header is verified and before anything is written — so a sender without the secret
 * cannot spend it, and a runaway or leaked one fires at most this many tasks a minute. A native
 * 60 s budget fails open on a binding error, like every other.
 */
export const AUTOMATION_WEBHOOK_PER_AUTOMATION_LIMIT = 60;

/**
 * Verified Managed Agents deliveries one hosted agent connection accepts per minute (#5083 decision
 * 166), counted on the native per-user binding of this budget keyed by the connection's id, after
 * the signature is verified and before the vendor or any conversation is reached — so a sender
 * without the secret cannot spend it. Sized so a busy connection is never refused into the
 * failures that disable its endpoint at the vendor: every delivery that passes it costs one read
 * of the vendor's read budget for the connection's key (the route's session read), and that budget
 * is 1,200 a minute per organization (the woken drain's event list spends more of it), so the key's
 * reads run out no later than the connection reaches this. A native 60 s budget fails open on a binding error, like every other.
 */
export const MANAGED_AGENTS_WEBHOOK_PER_CONNECTION_LIMIT = 1200;

/**
 * Paths that should be excluded from rate limiting entirely
 */
const EXCLUDED_PATHS = new Set(['/health']);

/**
 * Check if a path matches a pattern
 * Supports wildcards (*) at the end of patterns
 *
 * Examples:
 * - '/api/billing/*' matches '/api/billing', '/api/billing/', and '/api/billing/foo'
 * - '/api/billing/*' does NOT match '/api/billing-new'
 */
function matchPath(pattern: string, path: string): boolean {
  // Normalize: remove trailing slash for consistent matching
  const normalizedPath = path.replace(/\/$/, '');

  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -2); // Remove '/*'
    return normalizedPath === prefix || normalizedPath.startsWith(prefix + '/');
  }
  return pattern === normalizedPath;
}

/**
 * Get rate limit configuration for a specific path
 * Returns null if the path is excluded from rate limiting
 */
export function getEndpointConfig(path: string): EndpointRateLimitConfig | null {
  // Check exclusions first
  for (const excluded of EXCLUDED_PATHS) {
    if (matchPath(excluded, path)) {
      return null;
    }
  }

  // An exact key names its one path, so it wins over every wildcard covering that path —
  // including the wildcard whose prefix it is, which is the longer pattern and would win
  // below (`/api/calls/*` matches `/api/calls` too).
  const normalizedPath = path.replace(/\/$/, '');
  if (!normalizedPath.endsWith('/*') && Object.prototype.hasOwnProperty.call(ENDPOINT_CONFIGS, normalizedPath)) {
    return ENDPOINT_CONFIGS[normalizedPath];
  }

  // Find matching config (most specific first - longer patterns)
  const sortedPatterns = Object.keys(ENDPOINT_CONFIGS).sort((a, b) => b.length - a.length);

  for (const pattern of sortedPatterns) {
    if (matchPath(pattern, path)) {
      return ENDPOINT_CONFIGS[pattern];
    }
  }

  // Default config for unlisted endpoints
  return { tier: 'STANDARD' };
}

/**
 * Get the rate limit tier configuration
 */
export function getTierConfig(tier: RateLimitTier): (typeof RATE_LIMIT_TIERS)[RateLimitTier] {
  return RATE_LIMIT_TIERS[tier];
}

/**
 * Distinct per-user rate-limit budgets whose window is 60s (`userWindow` unset or 60),
 * across every endpoint config, plus `AUTOMATION_WEBHOOK_PER_AUTOMATION_LIMIT` and
 * `MANAGED_AGENTS_WEBHOOK_PER_CONNECTION_LIMIT`, which key the same bindings by an automation and
 * by a hosted agent connection. These are exactly the budgets that MUST have a native
 * `[[ratelimits]]` binding (see runtime `NATIVE_USER_LIMIT_BINDINGS`); the hourly
 * (`userWindow: 3600`) budgets are deliberately excluded — they stay Redis-only. Exported
 * so a CI test can assert the native binding map covers every 60s budget, surfacing drift
 * the moment a new 60s `userLimit` is added instead of failing silently in the shadow/
 * Phase-2 enforcement path.
 */
export function getSixtySecondUserLimits(): number[] {
  // Keyed by an automation rather than a person, but on the same native bindings.
  const limits = new Set<number>([AUTOMATION_WEBHOOK_PER_AUTOMATION_LIMIT, MANAGED_AGENTS_WEBHOOK_PER_CONNECTION_LIMIT]);
  for (const config of Object.values(ENDPOINT_CONFIGS)) {
    if (config.userLimit !== undefined && (config.userWindow ?? 60) === 60) {
      limits.add(config.userLimit);
    }
  }
  return [...limits].sort((a, b) => a - b);
}
